import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, HolderOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Checkbox, Empty, Input, Modal, Popconfirm, Select, message } from 'antd';
import { debounce, sortBy } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router-dom';
import { formDataToNestedJson } from '../../../library/utilities/formdata.js';
import { CustomSpecificationForm, ValidationForm as Form } from '../../form';
import { GeneralFieldWrapper, InputWrapper } from '../../inputs';
import { CancelButton } from '../../../ui';
import styles from './specifciations.module.scss';

const SPECTYPES = Object.freeze({
    INBUILT: 'inbuilt',
    CUSTOM: 'custom',
    HIDDEN: 'hidden',
})

function getSiblings(element) {
    const siblings = [];
    const parent = element.parentElement;
    const children = parent.children;

    for (let i = 0; i < children.length; i++) {
        const sibling = children[i];
        if (sibling !== element && sibling.classList.contains(element.classList[0])) {
            siblings.push(sibling);
        }
    }

    return siblings;
}
// Hidden-spec actions sit side by side in the row (the shared .editAction
// class is absolutely positioned and would stack them on one spot).
const hiddenActionsStyle = { display: 'flex', alignItems: 'center', flexShrink: 0 }
const hiddenActionStyle = { cursor: 'pointer', padding: '4px 6px', fontSize: '15px' }

const HiddenSpecification = ({ spec, selected = false, effectiveLabel, effectiveValue, onSpecificationChange, onEdit, onRemove, readonly = false }) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState({ label: '', value: '' })
    const canEdit = selected && !readonly && onEdit
    const startEditing = () => {
        setDraft({ label: effectiveLabel ?? '', value: effectiveValue ?? '' })
        setEditing(true)
    }
    // Typing the product default back (or clearing) removes the override.
    const save = () => {
        setEditing(false)
        const label = draft.label.trim()
        const value = draft.value.trim()
        onEdit({
            name_text: label === '' || label === spec.text ? '' : label,
            value_text: value === '' || value === spec.value_text ? '' : value,
        })
    }
    const onKeyDown = (e) => {
        if (e.key === 'Enter') save()
        if (e.key === 'Escape') setEditing(false)
    }
    return (
        <>
            <div className={styles.itemLabel}>
                {editing ? (
                    <Input
                        autoFocus
                        value={draft.label}
                        placeholder={spec.text}
                        onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
                        onKeyDown={onKeyDown}
                    />
                ) : (
                    <Checkbox defaultChecked={selected} disabled={readonly} onChange={e => {
                        onSpecificationChange(e.target.checked)
                    }}>{effectiveLabel ?? spec.text}</Checkbox>
                )}
            </div>
            <div className={styles.itemValue} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {editing ? (
                    <>
                        <Input.TextArea
                            value={draft.value}
                            placeholder={spec.value_text}
                            autoSize={{ minRows: 1, maxRows: 6 }}
                            onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save() } else if (e.key === 'Escape') setEditing(false) }}
                        />
                        <span style={hiddenActionsStyle}>
                            <CheckOutlined style={{ ...hiddenActionStyle, color: '#52c41a' }} title="Save" onClick={save} />
                            <CloseOutlined style={hiddenActionStyle} title="Cancel" onClick={() => setEditing(false)} />
                        </span>
                    </>
                ) : (
                    <>
                        <span style={{ flex: 1, minWidth: 0 }}>{effectiveValue ?? spec.value_text}</span>
                        <span style={hiddenActionsStyle}>
                        {canEdit && <EditOutlined style={hiddenActionStyle} title="Edit label and value" onClick={startEditing} />}
                        {!readonly && onRemove && (
                            <Popconfirm
                                title="Remove hidden specification"
                                description={`Remove "${effectiveLabel ?? spec.text}" from this item? You can restore it later.`}
                                onConfirm={(e) => {
                                    e?.stopPropagation?.()
                                    onRemove(spec.id)
                                }}
                                onCancel={(e) => e?.stopPropagation?.()}
                                okText="Yes"
                                cancelText="No"
                            >
                                <DeleteOutlined
                                    style={{ ...hiddenActionStyle, color: '#ff4d4f' }}
                                    title="Remove hidden specification"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Popconfirm>
                        )}
                        </span>
                    </>
                )}
            </div>
        </>
    )
}
HiddenSpecification.propTypes = {
    spec: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    effectiveLabel: PropTypes.string,
    effectiveValue: PropTypes.string,
    onSpecificationChange: PropTypes.func.isRequired,
    onEdit: PropTypes.func,
    onRemove: PropTypes.func,
    readonly: PropTypes.bool,
}

const getHiddenRow = (spec, hiddenSpecificationValues) =>
    (hiddenSpecificationValues || []).find(a => Number(a?.spec_id) === Number(spec.id))
const overrideText = (text) => typeof text === 'string' && text.trim() !== '' ? text.trim() : null

// Effective hidden value: per-item free-text override when set, else product default.
const getHiddenEffectiveValue = (spec, hiddenSpecificationValues) =>
    overrideText(getHiddenRow(spec, hiddenSpecificationValues)?.value_text) ?? spec.value_text

// Effective hidden label: per-item label override when set, else the spec name.
const getHiddenEffectiveLabel = (spec, hiddenSpecificationValues) =>
    overrideText(getHiddenRow(spec, hiddenSpecificationValues)?.name_text) ?? spec.text

const isEmptySpecValue = (value) => value === null || value === undefined || value === ''
// View mode never keeps a key with an empty value.
const hasVisibleSpecValue = (spec) => spec.type === SPECTYPES.INBUILT
    ? !isEmptySpecValue(spec.selectedValue?.text)
    : !isEmptySpecValue(spec.value)

const InbuiltSpecification = ({ spec, onSpecificationChange, onDelete, readonly = false }) => {
    return (
        <>
            <div className={styles.itemLabel}>
                <HolderOutlined style={{ 'color': '#8f8f8f', margin: '0 10px 0 0' }} />{spec.text}
            </div>
            <div className={styles.itemValue} style={{ display: 'flex', alignItems: 'center' }}>
                <Select
                    options={spec.values}
                    defaultValue={spec.valueId ?? null}
                    fieldNames={{ label: 'text', value: 'id' }}
                    variant="borderless"
                    style={{ flex: 1, minWidth: 0 }}
                    placeholder="Select"
                    onChange={(value) => onSpecificationChange(value)}
                    allowClear
                    disabled={readonly}
                />
                {!readonly && onDelete && (
                    <Popconfirm
                        title="Delete Specification"
                        description={`Are you sure you want to remove "${spec.text || spec.name}" from this item?`}
                        onConfirm={(e) => {
                            e?.stopPropagation?.()
                            onDelete(spec.id)
                        }}
                        onCancel={(e) => e?.stopPropagation?.()}
                        okText="Yes"
                        cancelText="No"
                    >
                        <DeleteOutlined
                            style={{
                                color: '#ff4d4f',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '15px',
                            }}
                            title="Delete specification"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Popconfirm>
                )}
            </div>
        </>
    )
}
InbuiltSpecification.propTypes = {
    spec: PropTypes.object.isRequired,
    onSpecificationChange: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    readonly: PropTypes.bool,
}

const CustomSpecification = ({ spec, onEdit }) => (
    <>
        <div className={styles.itemLabel}>
            <HolderOutlined style={{ 'color': '#8f8f8f', margin: '0 10px 0 0' }} /> {spec.name}
        </div>
        <div className={styles.itemValue}>
            {spec.value}
            <EditOutlined className={styles.editAction} onClick={onEdit} />
        </div>
    </>
)
CustomSpecification.propTypes = {
    spec: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
}

const CustomSpecificationModal = ({ selectedSpecification = null, isLoading = false, onClose, onDelete, onSave }) => {
    const [form] = Form.useForm()

    return (
        <Modal
            title={!selectedSpecification?.id ? "Add New Specification" : 'Update Specification'}
            open={true}
            width={600}
            confirmLoading={isLoading}
            // stays open while saving; closes once the save is done
            onCancel={isLoading ? undefined : onClose}
            closable={!isLoading}
            maskClosable={!isLoading}
            footer={[
                ...[!!selectedSpecification?.id && <Button key="delete" danger disabled={isLoading} onClick={() => onDelete(selectedSpecification.id)}>Delete</Button>],
                <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>Save</Button>,
                <CancelButton key="cancel" disabled={isLoading} onClick={onClose}>Cancel</CancelButton>,
            ]}
            destroyOnClose
        >
            <CustomSpecificationForm form={form} entityId={selectedSpecification?.id} initialValues={selectedSpecification} onFinish={onSave} preserve={false} />
        </Modal>
    )
}
CustomSpecificationModal.propTypes = {
    selectedSpecification: PropTypes.object,
    isLoading: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
}

const RestoreSpecificationModal = ({
    open,
    deletedInbuiltSpecifications = [],
    deletedHiddenSpecifications = [],
    isLoading = false,
    restoringSpecId = null,
    onClose,
    onRestore,
    onRestoreHidden
}) => {
    // One list: standard specs first, then hidden ones (tagged), same row UI.
    const restorable = [
        ...deletedInbuiltSpecifications.map(spec => ({ spec, hidden: false })),
        ...deletedHiddenSpecifications.map(spec => ({ spec, hidden: true })),
    ]
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UndoOutlined style={{ color: '#1677ff' }} />
                    <span>Restore Specifications</span>
                </div>
            }
            open={open}
            width={440}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>
            ]}
            destroyOnClose
        >
            {restorable.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '8px 0' }}>
                    <div style={{ color: '#666', fontSize: '13px' }}>
                        The following default specifications were deleted for this item. Click <strong>Restore</strong> to re-add any specification:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                        {restorable.map(({ spec, hidden }) => (
                            <div
                                key={`${hidden ? 'hidden' : 'inbuilt'}-${spec.id}`}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #e8e8e8',
                                    backgroundColor: '#fafafa',
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#262626' }}>
                                        {spec.text || spec.name}
                                    </span>
                                    {hidden && (
                                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                            Hidden specification
                                        </span>
                                    )}
                                    {spec.values?.length > 0 && (
                                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                            {spec.values.length} option{spec.values.length > 1 ? 's' : ''} available
                                        </span>
                                    )}
                                </div>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<UndoOutlined />}
                                    loading={restoringSpecId === `${hidden ? 'hidden' : 'inbuilt'}-${spec.id}`}
                                    disabled={isLoading}
                                    onClick={() => hidden ? onRestoreHidden?.(spec.id) : onRestore(spec.id)}
                                >
                                    Restore
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No deleted specifications to restore. All default specifications are currently active on this item."
                    />
                </div>
            )}
        </Modal>
    )
}
RestoreSpecificationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    deletedInbuiltSpecifications: PropTypes.array,
    deletedHiddenSpecifications: PropTypes.array,
    isLoading: PropTypes.bool,
    restoringSpecId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onRestore: PropTypes.func.isRequired,
    onRestoreHidden: PropTypes.func,
}

const ProjectItemSpecifications = ({
    children,
    quantity,
    print_position,
    max_print_area_size,
    inbuiltSpecifications = [],
    deletedInbuiltSpecificationIds = [],
    hiddenSpecifications = [],
    specificationValues = [],
    hiddenSpecificationValues = [],
    customSpecifications = [],
    readonly = false,
    onSpecificationsChange,
    onInbuiltSpecificationDelete,
    onCustomSpecificationAdd,
    onCustomSpecificationUpdate,
    onCustomSpecificationDelete,
    onHiddenSpecificationUpdate,
    onHiddenSpecificationEdit,
    onHiddenSpecificationRemove,
    onOrderChange,
    onDetailsChange
}) => {
    const [addSpecificationModalVisible, SetAddSpecificationModalVisible] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedCustomSpecification, setSelectedCustomSpecification] = useState(null)
    const selectedSpecs = useRef({})
    const dragSourceIndex = useRef(null)
    const dragTargetIndex = useRef(null)
    const fetcher = useFetcher()

    const deletedSpecIdSet = useMemo(() => new Set(
        (deletedInbuiltSpecificationIds || []).map(Number)
    ), [deletedInbuiltSpecificationIds])
    const activeInbuiltSpecifications = useMemo(() => (
        (inbuiltSpecifications || []).filter(spec => !deletedSpecIdSet.has(Number(spec.id)))
    ), [inbuiltSpecifications, deletedSpecIdSet])

    useEffect(() => {
        const _mergedSpecs = mergedSpecs()
        selectedSpecs.current = activeInbuiltSpecifications.reduce((prev, { id }) => {
            const _selectedSpec = specificationValues.find(({ id: _id }) => _id === id)
            return {
                ...prev,
                [id]: {
                    valueId: _selectedSpec?.value?.id,
                    sort_order: _selectedSpec?.value?.sort_order ?? _mergedSpecs.findIndex(({ id: _id }) => _id === id)
                }
            }
        }, {})
    }, [activeInbuiltSpecifications, specificationValues])


    const mergedSpecs = useCallback(() => {
        return sortBy([
            ...activeInbuiltSpecifications.map((spec, index) => ({
                ...spec,
                valueId: specificationValues.find(({ id }) => id === spec.id)?.value?.id,
                selectedValue: specificationValues.find(({ id }) => id === spec.id)?.value,
                ['type']: SPECTYPES.INBUILT,
                ['sort_order']: specificationValues.find(({ id }) => id === spec.id)?.sort_order ?? index
            })),
            // ...hiddenSpecifications.map((spec, index) => ({
            //     ...spec,
            //     name: spec.text,
            //     value: spec.value_text,
            //     type: SPECTYPES.HIDDEN,
            //     sort_order: spec.sort_order ?? index
            // })),
            ...customSpecifications.map((spec, index) => ({ ...spec, ['type']: SPECTYPES.CUSTOM, ['sort_order']: spec.sort_order ?? index }))
        ], 'sort_order')
    }, [activeInbuiltSpecifications, /* hiddenSpecifications, */ specificationValues, customSpecifications])

    const onDragStart = (event, index) => {
        dragSourceIndex.current = index
        const elem = event.currentTarget //Source element
        setTimeout(() => elem.firstChild.style.visibility = 'hidden', 1)
    }

    const onDragEnter = (event, index) => {
        dragTargetIndex.current = index
        if (dragSourceIndex.current === index) {
            return true
        }
        const elem = event.currentTarget //Target element
        const insertBefore = dragTargetIndex.current < dragSourceIndex.current;
        const shiftAmount = insertBefore ? elem.offsetHeight : -elem.offsetHeight
        elem.style.transform = `translateY(${shiftAmount}px)`
        getSiblings(elem).forEach((a, i) => {
            let translateY = 0
            if (insertBefore) {
                translateY = (i >= dragTargetIndex.current && i < dragSourceIndex.current) ? shiftAmount : 0
            }
            else {
                translateY = (i < dragTargetIndex.current && i > dragSourceIndex.current) ? shiftAmount : 0
            }
            a.style.transform = `translateY(${translateY}px)`
        });
    }

    const resetDraggedElements = (elem) => {
        elem.firstChild.style.visibility = 'visible';
        [elem, ...getSiblings(elem)].forEach(a => {
            a.style.transform = 'translateY(0px)'
        })
        dragSourceIndex.current = null
        dragTargetIndex.current = null
    }

    const onDragEnd = (event) => {
        if (onOrderChange) {
            if (dragSourceIndex.current === null || dragTargetIndex.current === null) {
                resetDraggedElements(event.currentTarget)
                return
            }
            const _mergedSpecs = mergedSpecs()
            const [sourceItem] = _mergedSpecs.splice(dragSourceIndex.current, 1)
            _mergedSpecs.splice(dragTargetIndex.current, 0, sourceItem)
            let _customSpecifications = []
            _mergedSpecs.forEach((spec, index) => {
                const specType = spec.type
                delete spec.type
                spec.sort_order = index
                switch (specType) {
                    case SPECTYPES.INBUILT: {
                        selectedSpecs.current[spec.id].sort_order = index
                        break
                    }

                    case SPECTYPES.CUSTOM: {
                        _customSpecifications.push(spec)
                        break
                    }
                    default:
                        throw new Error('Invalid Specification Type')
                }
            })

            onOrderChange(selectedSpecs.current, _customSpecifications)
        }
        resetDraggedElements(event.currentTarget)
    }

    const handleSpecificationChange = (spec, sort_order, value) => {
        if (readonly) {
            return true
        }
        selectedSpecs.current[spec] = {
            valueId: value,
            sort_order
        }
        if (onSpecificationsChange) {
            onSpecificationsChange(selectedSpecs.current)
        }
    }

    const handleInbuiltSpecificationDelete = async (specId) => {
        if (readonly || !onInbuiltSpecificationDelete) return
        try {
            await onInbuiltSpecificationDelete(specId)
        } catch {
            message.error('Unable to delete specification')
        }
    }

    const handleHiddenSpecificationChange = (spec, selected) => {
        if (readonly) {
            return true
        }
        if (onHiddenSpecificationUpdate) {
            onHiddenSpecificationUpdate(spec, selected)
        }
    }

    const handleHiddenSpecificationEdit = (specId, overrides) => {
        if (readonly || !onHiddenSpecificationEdit) {
            return true
        }
        onHiddenSpecificationEdit(specId, overrides)
    }

    const handleHiddenSpecificationRemove = async (specId) => {
        if (readonly || !onHiddenSpecificationRemove) return
        try {
            await onHiddenSpecificationRemove(specId)
        } catch {
            message.error('Unable to remove hidden specification')
        }
    }

    const handleAddCustomeSpecificationClose = () => {
        if (readonly) {
            return true
        }
        SetAddSpecificationModalVisible(false)
        setSelectedCustomSpecification(null)
    }

    const handleCustomSpecificationSubmit = async (data, specId, errorHandler) => {
        if (readonly) {
            return true
        }
        setIsLoading(true)
        try {
            specId ?
                await onCustomSpecificationUpdate(specId, { ...data }) :
                await onCustomSpecificationAdd({ ...data, ['sort_order']: mergedSpecs().length })
            setIsLoading(false);
            SetAddSpecificationModalVisible(false)

        }
        catch (e) {
            setIsLoading(false)
            // the save action already showed its error: just keep the dialog open
            if (e?.handled) {
                return
            }
            if (!errorHandler?.(e)) {
                message.error('Unable to save specification')
                if (e.response?.data?.message) {
                    message.error(e.response.data.message)
                }
            }
        }

    }

    const handleCustomSpecificationDelete = async (_specId) => {
        if (readonly) {
            return true
        }
        setIsLoading(true)
        try {
            await onCustomSpecificationDelete(_specId)
            SetAddSpecificationModalVisible(false)
            setIsLoading(false)
            setSelectedCustomSpecification(null)
        }
        catch (e) {
            setIsLoading(false)
            if (!e?.handled) {
                message.error('Unable to delete specification')
            }
        }

    }
    // const submitDetails = (value) => {
    //     // fetcher.submit({
    //     //     ...value,
    //     //     action: 'updateItem'
    //     // }, {
    //     //     method: 'post',
    //     //     encType: 'application/json',
    //     // })
    // }

    const debouncedSubmitDetails = !!onDetailsChange && debounce(onDetailsChange, 800)

    if (readonly) {
        return (
            <div className={`${styles.wrapper} ${styles.readonly}`}>
                {mergedSpecs()?.filter(hasVisibleSpecValue).map((spec) => {
                    switch (spec.type) {
                        case SPECTYPES.INBUILT: {
                            return (
                                <div className={styles.item} key={spec.id}>
                                    <div className={styles.itemLabel}>
                                        {spec.text}
                                    </div>
                                    <div className={styles.itemValue}>
                                        {spec.selectedValue?.text}
                                    </div>
                                </div>
                            )
                        }

                        case SPECTYPES.CUSTOM:
                            return (
                                <div className={styles.item} key={spec.id} >
                                    <div className={styles.itemLabel}>
                                        {spec.name}
                                    </div>
                                    <div className={styles.itemValue}>
                                        {spec.value}
                                    </div>
                                </div>
                            )

                        default:
                            return ''

                    }
                })}
                {hiddenSpecifications.filter(s => hiddenSpecificationValues?.some(a => (a.spec_id === s.id)) && !isEmptySpecValue(getHiddenEffectiveValue(s, hiddenSpecificationValues)))?.map(spec => (
                    <div className={styles.item} key={spec.id} >
                        <div className={styles.itemLabel}>
                            {getHiddenEffectiveLabel(spec, hiddenSpecificationValues)}
                        </div>
                        <div className={styles.itemValue}>
                            {getHiddenEffectiveValue(spec, hiddenSpecificationValues)}
                        </div>
                    </div>
                ))}
                {!!quantity && (
                    <div className={styles.item}>
                        <div className={styles.itemLabel}>Quantity</div>
                        <div className={styles.itemValue}>{quantity}</div>
                    </div>
                )}
                <div className={styles.specBottom}>
                    {!!print_position && <GeneralFieldWrapper label="Print Position">
                        <span className={styles.itemValue}>{print_position}</span>
                    </GeneralFieldWrapper>}
                    {!!max_print_area_size && <GeneralFieldWrapper label="Max Print Area Size">
                        <span className={styles.itemValue}>{max_print_area_size}</span>
                    </GeneralFieldWrapper>}
                </div>
                {children}
            </div >
        )
    }

    return (
        <div className={styles.wrapper}>
            {mergedSpecs()?.map((spec, index) => (
                <div className="specItemWrapper" key={spec.id} draggable onDragStart={e => onDragStart(e, index, spec)} onDragEnter={e => onDragEnter(e, index, spec)} onDragEnd={e => onDragEnd(e, index, spec)}>
                    <div className={styles.item}>
                        {spec.type === SPECTYPES.INBUILT ?
                            <InbuiltSpecification
                                spec={spec}
                                readonly={readonly}
                                onSpecificationChange={val => handleSpecificationChange(spec.id, spec.sort_order, val)}
                                onDelete={onInbuiltSpecificationDelete ? (specId) => handleInbuiltSpecificationDelete(specId) : null}
                            /> :
                            <CustomSpecification spec={spec} onEdit={() => {
                                setSelectedCustomSpecification(spec.id)
                                SetAddSpecificationModalVisible(true)
                            }} />
                        }
                    </div>
                </div>
            ))}
            {hiddenSpecifications?.map(spec => (
                <div className="specItemWrapper" key={spec.id}>
                    <div className={`${styles.item} ${styles.itemHidden}`}>
                        <HiddenSpecification
                            key={spec.id}
                            spec={spec}
                            selected={hiddenSpecificationValues?.some(a => (a.spec_id === spec.id))}
                            effectiveLabel={getHiddenEffectiveLabel(spec, hiddenSpecificationValues)}
                            effectiveValue={getHiddenEffectiveValue(spec, hiddenSpecificationValues)}
                            onSpecificationChange={selected => handleHiddenSpecificationChange(spec.id, selected)}
                            onEdit={onHiddenSpecificationEdit ? (overrides) => handleHiddenSpecificationEdit(spec.id, overrides) : null}
                            onRemove={onHiddenSpecificationRemove ? (specId) => handleHiddenSpecificationRemove(specId) : null}
                            readonly={readonly}
                        />
                    </div>
                </div>
            ))}
            {!!quantity && (
                <div className="specItemWrapper">
                    <div className={styles.item}>
                        <div className={styles.itemLabel}>Quantity</div>
                        <div className={styles.itemValue}>{quantity}</div>
                    </div>
                </div>
            )}
            <fetcher.Form onChange={(e) => {
                if (!onDetailsChange) {
                    return
                }
                const data = formDataToNestedJson(new FormData(e.currentTarget))
                debouncedSubmitDetails(data)
            }}>
                <div className={styles.specBottom}>
                    <InputWrapper inputKey='print_position' name='print_position' defaultValue={print_position} label="Print Position" />
                    <InputWrapper inputKey='max_print_area_size' name='max_print_area_size' defaultValue={max_print_area_size} label="Max Print Area Size" rules={[{ required: true, message: 'Please Enter Max Print Area Size!' }]} />
                </div>
            </fetcher.Form>


            <Button style={{ 'margin': '10px 1% 1rem auto', 'width': '49%' }} icon={<PlusOutlined />} onClick={() => SetAddSpecificationModalVisible(true)}>Add Specification</Button>
            {children}
            {!!addSpecificationModalVisible && (
                <CustomSpecificationModal
                    selectedSpecification={selectedCustomSpecification ? customSpecifications.find(a => a.id === selectedCustomSpecification) : null}
                    isLoading={isLoading}
                    onClose={handleAddCustomeSpecificationClose}
                    onDelete={handleCustomSpecificationDelete}
                    onSave={handleCustomSpecificationSubmit}
                />
            )}
        </div >
    )
}

ProjectItemSpecifications.propTypes = {
    children: PropTypes.node,
    inbuiltSpecifications: PropTypes.arrayOf(PropTypes.object),
    deletedInbuiltSpecificationIds: PropTypes.array,
    specificationValues: PropTypes.arrayOf(PropTypes.object),
    hiddenSpecificationValues: PropTypes.arrayOf(PropTypes.object),
    hiddenSpecifications: PropTypes.arrayOf(PropTypes.object),
    customSpecifications: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        name_default: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        value_default: PropTypes.string.isRequired,
        sort_order: PropTypes.number,
    })),
    max_print_area_size: PropTypes.string,
    print_position: PropTypes.string,
    onSpecificationsChange: PropTypes.func,
    onInbuiltSpecificationDelete: PropTypes.func,
    onCustomSpecificationAdd: PropTypes.func,
    onCustomSpecificationUpdate: PropTypes.func,
    onCustomSpecificationDelete: PropTypes.func,
    onHiddenSpecificationUpdate: PropTypes.func,
    onHiddenSpecificationEdit: PropTypes.func,
    onHiddenSpecificationRemove: PropTypes.func,
    onOrderChange: PropTypes.func,
    onDetailsChange: PropTypes.func,
    readonly: PropTypes.bool,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export { RestoreSpecificationModal }
export default ProjectItemSpecifications
